// src/components/PersonalizedStartCard.jsx
// ─────────────────────────────────────────────────────────────────────────────
// §4 du MD onboarding — personnalisation du Dashboard.
// Le socle du dashboard reste identique ; ce composant n'ajoute qu'une bande de
// personnalisation en tête : raccourcis + conseil dérivés des RÉPONSES d'onboarding
// (profiles.onboarding_answers) et du type métier. Rien de sensible : lecture seule
// du profil déjà chargé par AuthContext.
//
// Masquable (localStorage, par utilisateur). Ne s'affiche que si l'utilisateur a
// des données d'onboarding (sinon aucun signal à personnaliser).
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { resolveProfileType, getAccountState, metierCount, isLightAccount } from "../utils/onboardingProfile";

// Construit { shortcuts:[{key,to}], adviceKey } à partir des réponses.
function buildPersonalization(profileType, answers) {
  const a = answers || {};

  if (profileType === "collectionneur") {
    const shortcuts = [];
    // Raccourci principal selon l'objectif déclaré
    switch (a.main_goal) {
      case "transmission": shortcuts.push({ key: "succession", to: "/succession" }); break;
      case "insure":       shortcuts.push({ key: "insure", to: "/insurance" }); break;
      case "market":       shortcuts.push({ key: "market", to: "/art-market-insights" }); break;
      default:             shortcuts.push({ key: "addWork", to: "/add" });
    }
    // Reprendre l'existant si les documents ne sont pas déjà centralisés
    if (["paper", "scattered", "cloud"].includes(a.documents_location)) {
      shortcuts.push({ key: "deposit", to: "/onboarding/deposit" });
    }
    shortcuts.push({ key: "estimate", to: "/estimation" });

    // Conseil (un seul, par priorité)
    let adviceKey = "generic";
    if (a.wants_support === true) adviceKey = "support";
    else if (["paper", "scattered"].includes(a.documents_location)) adviceKey = "centralize";
    else if (a.main_goal === "transmission") adviceKey = "transmission";
    else if (a.main_goal === "insure") adviceKey = "insure";
    else if (a.main_goal === "market") adviceKey = "market";
    else if (a.main_goal === "inventory") adviceKey = "inventory";

    return { shortcuts: shortcuts.slice(0, 3), adviceKey };
  }

  // Autres métiers : raccourci vers l'espace + reprise de l'existant.
  const HOME = {
    advisor: "/expert/dashboard",
    family_office: "/family-office/manage",
    fondation: "/foundation/dashboard",
    notaire: "/notaire",
    assureur: "/insurance",
  };
  return {
    shortcuts: [
      { key: "home", to: HOME[profileType] || "/my-collection" },
      { key: "importExisting", to: "/onboarding/deposit" },
    ],
    adviceKey: "generic",
  };
}

export default function PersonalizedStartCard() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [hidden, setHidden] = useState(false);

  const [state, setState] = useState(null); // état du compte (compteurs)
  const [loaded, setLoaded] = useState(false);

  const profileType = useMemo(() => resolveProfileType(profile), [profile]);
  const answers = useMemo(() => profile?.onboarding_answers || {}, [profile?.onboarding_answers]);

  const dismissKey = profile?.id ? `artkiv_start_card_${profile.id}` : null;
  const alreadyDismissed = dismissKey ? localStorage.getItem(dismissKey) === "1" : false;

  const perso = useMemo(() => buildPersonalization(profileType, answers), [profileType, answers]);

  // On aide TANT QUE le compte est léger (peu de contenu), pas seulement après
  // l'onboarding : un utilisateur avec 1 œuvre a encore besoin d'un coup de pouce.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await getAccountState();
      if (!cancelled) { setState(s); setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [profile?.id]);

  const count = metierCount(state, profileType);
  const light = isLightAccount(state, profileType);

  if (!profile || hidden || alreadyDismissed) return null;
  if (!loaded) return null;          // on attend l'état pour éviter le clignotement
  if (!light) return null;           // compte établi → plus de carte d'aide
  if (!perso.shortcuts.length) return null;

  const dismiss = () => {
    setHidden(true);
    try { if (dismissKey) localStorage.setItem(dismissKey, "1"); } catch (_) {}
  };

  const firstName = profile.first_name || "";

  return (
    <div className="mb-8 bg-white border border-gray-100 rounded-2xl p-6 md:p-7 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[0.7rem] tracking-widest uppercase text-amber-700 mb-1">
            {t("onboarding.dashboard.title")}
          </p>
          <h2 className="text-xl md:text-2xl font-light text-gray-900" style={{ fontFamily: "Georgia, serif" }}>
            {firstName
              ? t("onboarding.dashboard.helloName", { name: firstName })
              : t("onboarding.dashboard.hello")}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t(`onboarding.dashboard.stage.${profileType}`, { count })}
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-xs text-gray-400 hover:text-gray-600 shrink-0"
          aria-label={t("onboarding.dashboard.dismiss")}
        >
          {t("onboarding.dashboard.dismiss")} ✕
        </button>
      </div>

      {/* Conseil personnalisé */}
      <div className="mt-4 flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
        <span aria-hidden>💡</span>
        <p className="text-sm text-emerald-900 leading-relaxed">
          {t(`onboarding.dashboard.advice.${perso.adviceKey}`)}
        </p>
      </div>

      {/* Raccourcis personnalisés */}
      <p className="mt-5 text-[0.7rem] tracking-widest uppercase text-gray-400">
        {t("onboarding.dashboard.shortcutsLabel")}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {perso.shortcuts.map((s) => (
          <Link
            key={s.key}
            to={s.to}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-800 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition-all"
          >
            {t(`onboarding.dashboard.shortcuts.${s.key}`)}
            <span aria-hidden>→</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
